import React from 'react';
import './App.css';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AppBar from '@mui/material/AppBar';
import Snackbar from '@mui/material/Snackbar';
import CompositionModeDialog from './components/CompositionModeDialog';
import Toolbar from '@mui/material/Toolbar';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import PeaCharacter from './components/UI/PeaCharacter';
import AddAPhoto from '@mui/icons-material/AddAPhoto';
import AnimatedCanvas from './components/AnimatedCanvas';
import FramesCanvas from './components/FramesCanvas';
import PlaceholderSource from './components/PlaceholderSource';
import PlaceholderSelect from './components/PlaceholderSelect';
import Toolbox from './components/Toolbox';
import { TOOLS } from './constants/tool_constants';
import Alert from '@mui/material/Alert';
import { updateImageFrames } from './image_utilities/FramesUpdater';
import {
  processImageFile,
  validateFileIsImage,
  validateFileSize,
  getUniqueFileName,
  VALID_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
} from './image_utilities/ImageLoader';

const MAX_UNDO_STACK_LENGTH = 3;

const EYE_DROPPER_SOURCE = {
  NONE: 'none',
  PARTIFY: 'canvas',
  TRANSPARENT: 'transparent',
};

class App extends React.Component {
  state = {
    images: [],
    imageUndoStack: [],
    imageRedoStack: [],
    activeTool: TOOLS.NONE,
    isDrawerOpen: false,
    errorMessage: '',
    imagesAreLoading: false,
    hoverPixel: new Uint8ClampedArray(),
    selectedPixel: new Uint8ClampedArray(),
    eyeDropperIsEnabled: false,
    selectedImagesNames: {},
    selectedFrameIndices: [],
    canvasDimensions: { width: 450, height: 450 },
    canvasBackgroundColor: '#ffffff',
    currentTransparentColor: '',
    canShowSaveDialog: true,
    eyeDropperSource: EYE_DROPPER_SOURCE.NONE,
    canPlay: true,
    isPlaying: true,
    outputOptions: { durationInput: '', playbackMode: 'auto' },
    compositionMode: 'repeat',
    choosingCompositionMode: false,
    workingFrameOrder: [],
    workingDeletedFrameIndices: [],
    workingEmptiedFrameIndices: [],
    previewImage: null,
    isImageSelectOpen: false,
  };

  fileUpload = React.createRef();

  handlePlaybackChange = (isPlaying) => this.setState({ isPlaying });
  handleOutputOptionsChange = outputOptions => this.setState({ outputOptions });
  handleCompositionModeChange = compositionMode => this.setState({
    compositionMode, choosingCompositionMode: false,
    outputOptions: { durationInput: '', playbackMode: 'auto' },
  });

  handleFileChange = (e) => {
    this.setState({ imagesAreLoading: true });
    const file = this.fileUpload.current.files[0];
    if (!file) {
      this.setState({ imagesAreLoading: false });
      return;
    }
    if (!validateFileIsImage(file)) {
      this.setState({
        imagesAreLoading: false,
        errorMessage:
          'File extension not recognized.  Must be one of: ' + VALID_FILE_EXTENSIONS.join(', '),
      });
      return;
    }
    if (!validateFileSize(file)) {
      this.setState({
        imagesAreLoading: false,
        errorMessage:
          'File is too large. Max size is ' + Math.floor(MAX_FILE_SIZE / 1024 / 1024) + 'MB.',
      });
      return;
    }
    // Clear html input field
    e.target.value = null;

    processImageFile(file)
      .then((frames) => this.addImage(file.name, frames))
      .catch((error) => {
        this.setState({ imagesAreLoading: false, errorMessage: error.message });
      });
  };

  addImage = (fileName, frames) => {
    this.setState((prevState) => {
      // Ensure we don't have name duplicates
      const existingNames = prevState.images.map((image) => image.name);
      const name = getUniqueFileName(existingNames, fileName);

      const newState = {
        imagesAreLoading: false,
        choosingCompositionMode: prevState.images.length === 1,
        images: prevState.images.concat({
          frames,
          x: 0,
          y: 0,
          name,
          startIndex: 0,
          playCount: frames.playCount ?? 0,
        }),
      };

      if (prevState.images.length === 0) {
        newState.canvasDimensions = { width: frames[0].width, height: frames[0].height };
        newState.selectedImagesNames = { [name]: true };
        newState.selectedFrameIndices = [];
        // A replacement image starts a new frame-edit draft, even if the last
        // image was removed with unsubmitted edits or an active preview.
        newState.previewImage = null;
        newState.workingFrameOrder = [];
        newState.workingDeletedFrameIndices = [];
        newState.workingEmptiedFrameIndices = [];
      }

      return newState;
    });
  };

  handleToolChange = (newTool) => {
    this.setState((prevState) => ({
      activeTool: prevState.activeTool === newTool ? TOOLS.NONE : newTool,
      isDrawerOpen: prevState.activeTool !== newTool,
    }));
  };

  handleDrawerClose = () => {
    this.setState({
      isDrawerOpen: false,
      activeTool: TOOLS.NONE,
    });
  };

  clearAllImages = () => {
    this.setState({
      images: [],
      errorMessage: '',
    });
  };

  handlePlaceholderSourceClick = () => {
    this.fileUpload.current.click();
  };

  handlePlaceholderSelectClick = () => {
    this.setState({ isImageSelectOpen: true });
  };

  handleImageSelectToggle = () => {
    this.setState((prevState) => ({
      isImageSelectOpen: !prevState.isImageSelectOpen,
    }));
  };

  handleEyeDropperClick = () => {
    this.setState((prevState) => ({
      eyeDropperIsEnabled: !prevState.eyeDropperIsEnabled,
      eyeDropperSource: EYE_DROPPER_SOURCE.PARTIFY,
      canPlay: prevState.eyeDropperIsEnabled,
      selectedPixel: new Uint8ClampedArray(),
      hoverPixel: new Uint8ClampedArray(),
    }));
  };

  handleSaveEyeDropperClick = () => {
    this.setState({
      eyeDropperIsEnabled: true,
      eyeDropperSource: EYE_DROPPER_SOURCE.TRANSPARENT,
      canPlay: false,
      selectedPixel: new Uint8ClampedArray(),
      hoverPixel: new Uint8ClampedArray(),
      canShowSaveDialog: false,
    });
  };

  handleImageChange = (images) => {
    this.setState((prevState) => {
      return {
        images: prevState.images.reduce((acc, oldImage) => {
          const newImage = images.find((image) => image.name === oldImage.name);
          acc.push(newImage !== undefined ? newImage : oldImage);
          return acc;
        }, []),
        imageUndoStack: [
          prevState.images,
          ...prevState.imageUndoStack.slice(0, MAX_UNDO_STACK_LENGTH - 1),
        ],
        imageRedoStack: [],
        imagesAreLoading: false,
        workingDeletedFrameIndices: [],
        workingEmptiedFrameIndices: [],
        workingFrameOrder: [],
        selectedFrameIndices: [],
      };
    });
  };

  handleImageReorder = (images) => {
    this.setState((prevState) => ({
      images: images,
      imageUndoStack: [
        prevState.images,
        ...prevState.imageUndoStack.slice(0, MAX_UNDO_STACK_LENGTH - 1),
      ],
      imageRedoStack: [],
      imagesAreLoading: false,
    }));
  };

  handleImageRemoval = (imagesToRemove) => {
    this.setState((prevState) => ({
      images: prevState.images.reduce((acc, oldImage) => {
        const imageToRemove = imagesToRemove.find((image) => image.name === oldImage.name);
        if (imageToRemove === undefined) {
          acc.push(oldImage);
        }
        return acc;
      }, []),
      imagesAreLoading: false,
    }));
  };

  handleImageLoadStart = () => {
    this.setState({ imagesAreLoading: true });
  };

  handleImageMouseMove = (e, pixel) => {
    if (pixel.length > 0) {
      this.setState({ hoverPixel: pixel });
    }
  };

  handleImageClick = (e) => {
    this.setState((prevState) => ({
      selectedPixel: prevState.hoverPixel,
      eyeDropperIsEnabled: false,
      canPlay: true,
      canShowSaveDialog:
        prevState.eyeDropperSource === EYE_DROPPER_SOURCE.TRANSPARENT
          ? true
          : prevState.canShowSaveDialog,
    }));
  };

  handleChangeStartIndex = (index, delta) => {
    this.setState((prevState) => ({
      images: [
        ...prevState.images.slice(0, index),
        {
          ...prevState.images[index],
          startIndex: prevState.images[index].startIndex + delta,
        },
        ...prevState.images.slice(index + 1),
      ],
    }));
  };

  handleUndoClick = () => {
    if (this.state.imageUndoStack.length === 0) {
      return;
    }
    this.setState((prevState) => {
      const [images, ...imageUndoStack] = prevState.imageUndoStack;
      return {
        images,
        imageUndoStack,
        imageRedoStack: [prevState.images, ...prevState.imageRedoStack],
      };
    });
  };

  handleRedoClick = () => {
    if (this.state.imageRedoStack.length === 0) {
      return;
    }
    this.setState((prevState) => {
      const [images, ...imageRedoStack] = prevState.imageRedoStack;
      return {
        images,
        imageRedoStack,
        imageUndoStack: [
          prevState.images,
          ...prevState.imageUndoStack.slice(0, MAX_UNDO_STACK_LENGTH - 1),
        ],
      };
    });
  };

  getSelectedOrHoverPixel = () => {
    if (this.state.selectedPixel.length > 0) {
      return this.state.selectedPixel;
    } else if (this.state.eyeDropperIsEnabled) {
      return this.state.hoverPixel;
    }
    return new Uint8ClampedArray();
  };

  handleImageCheckboxChange = (name) => {
    this.setState((prevState) => {
      if (name in prevState.selectedImagesNames) {
        // Delete image with name from check images list
        const { [name]: nowUncheckedImageName, ...selectedImagesNames } =
          prevState.selectedImagesNames;
        return { selectedImagesNames, selectedFrameIndices: [] };
      }
      // Add image name to list
      return {
        selectedImagesNames: { ...prevState.selectedImagesNames, [name]: true },
        selectedFrameIndices: [],
      };
    });
  };

  getSelectedImages() {
    return this.state.images.reduce((selectedImages, image) => {
      if (image.name in this.state.selectedImagesNames) {
        return selectedImages.concat(image);
      }
      return selectedImages;
    }, []);
  }

  handleCanvasChange = (width, height, canvasBackgroundColor) => {
    this.setState({ canvasDimensions: { width, height }, canvasBackgroundColor });
  };

  handleCloseError = () => {
    this.setState({ errorMessage: '' });
  };

  handleFrameClick = (imageName, frameIndex) => {
    this.setState({
      selectedImagesNames: { [imageName]: true },
      selectedFrameIndices: [frameIndex],
      activeTool: TOOLS.FRAMES,
      isDrawerOpen: true,
    });
  };

  handleImageSelect = (name) =>
    this.setState({
      selectedImagesNames: name !== '' ? { [name]: true } : {},
      selectedFrameIndices: [],
      previewImage: null,
    });

  handleSelectedFrameChange = (selectedFrameIndex) => {
    this.setState((prevState) => ({
      selectedFrameIndices: this.addOrRemoveIfExists(
        selectedFrameIndex,
        prevState.selectedFrameIndices
      ),
    }));
  };

  handleWorkingFrameOrderChange = (newOrder) => {
    this.setState({ workingFrameOrder: newOrder });
  };

  handleWorkingDeletedFrameIndicesChange = (deletedFrameIndex) => {
    this.setState((prevState) => ({
      workingDeletedFrameIndices: this.addOrRemoveIfExists(
        deletedFrameIndex,
        prevState.workingDeletedFrameIndices
      ),
      selectedFrameIndices: this.removeIfExists(deletedFrameIndex, prevState.selectedFrameIndices),
    }));
  };

  handleWorkingEmptiedFrameIndicesChange = (emptiedFrameIndex) => {
    this.setState((prevState) => ({
      workingEmptiedFrameIndices: this.addOrRemoveIfExists(
        emptiedFrameIndex,
        prevState.workingEmptiedFrameIndices
      ),
    }));
  };

  removeIfExists = (x, arr) => {
    const index = arr.indexOf(x);
    const newArr = [...arr];
    if (index !== -1) {
      newArr.splice(index, 1);
    }
    return newArr;
  };

  addOrRemoveIfExists = (x, arr) => {
    const index = arr.indexOf(x);
    let newArr = [...arr];
    index === -1 ? newArr.push(x) : newArr.splice(index, 1);
    return newArr;
  };

  getFrameOrderWithoutDeleted = () => {
    if (this.getSelectedImages().length === 0) {
      return [];
    }

    let frameOrder = this.state.workingFrameOrder;
    if (frameOrder.length === 0) {
      frameOrder = this.getSelectedImages()[0].frames.map((_, index) => index);
    }
    return frameOrder.filter((x) => !this.state.workingDeletedFrameIndices.includes(x));
  };

  handleFramesChange = (newDelay, newX, newY) => {
    this.handleImageLoadStart();
    this.updateSelectedImageFrames(newDelay, newX, newY).then((newImage) => {
      this.handleImageChange([newImage]);
    });
  };

  updateSelectedImageFrames = (newDelay, newX, newY) => {
    const firstSeletedImage = this.getSelectedImages()[0];
    const updates = { delay: newDelay, x: newX, y: newY };
    const config = {
      selectedIndices: this.state.selectedFrameIndices,
      emptiedIndices: this.state.workingEmptiedFrameIndices,
      deletedIndices: this.state.workingDeletedFrameIndices,
      frameOrder: this.state.workingFrameOrder,
    };
    return updateImageFrames(firstSeletedImage, updates, config);
  };

  handleFramesPreview = (newDelay, newX, newY) => {
    this.handleImageLoadStart();
    this.updateSelectedImageFrames(newDelay, newX, newY).then((newImage) => {
      this.setState({
        previewImage: newImage,
        imagesAreLoading: false,
      });
    });
  };

  handleFramesPreviewExit = () => {
    this.setState({ previewImage: null });
  };

  renderContent() {
    const selectedImages = this.getSelectedImages();

    if (this.state.images.length > 0 && this.state.activeTool !== TOOLS.FRAMES) {
      return (
        <AnimatedCanvas
          images={this.state.images}
          onMouseMove={this.handleImageMouseMove}
          onClick={this.handleImageClick}
          canPlay={this.state.canPlay && !this.state.choosingCompositionMode}
          compositionMode={this.state.images.length > 1 ? this.state.compositionMode : 'source'}
          onCompositionModeChange={this.handleCompositionModeChange}
          initialIsPlaying={this.state.isPlaying}
          onPlaybackChange={this.handlePlaybackChange}
          initialOutputOptions={this.state.outputOptions}
          onOutputOptionsChange={this.handleOutputOptionsChange}
          canMoveImages={!this.state.eyeDropperIsEnabled}
          onClickPlay={this.handleClickPlay}
          onStartIndexChange={this.handleChangeStartIndex}
          width={this.state.canvasDimensions.width}
          height={this.state.canvasDimensions.height}
          backgroundColor={this.state.canvasBackgroundColor}
          onImageChange={this.handleImageChange}
          onImageChangeStart={this.handleImageLoadStart}
          onUndoClick={this.handleUndoClick}
          onRedoClick={this.handleRedoClick}
          onEyeDropperClick={this.handleSaveEyeDropperClick}
          canUndo={this.state.imageUndoStack.length > 0}
          canRedo={this.state.imageRedoStack.length > 0}
          canShowSaveDialog={this.state.canShowSaveDialog}
          selectedPixel={
            this.state.eyeDropperSource === EYE_DROPPER_SOURCE.TRANSPARENT
              ? this.state.selectedPixel
              : new Uint8ClampedArray()
          }
          showEyeDropperCursor={this.state.eyeDropperIsEnabled}
          onFrameClick={this.handleFrameClick}
        />
      );
    } else if (this.state.activeTool === TOOLS.FRAMES && this.state.images.length > 0) {
      return selectedImages.length > 0 ? (
        <FramesCanvas
          image={selectedImages[0]}
          selectedFrameIndices={this.state.selectedFrameIndices}
          mainCanvasWidth={this.state.canvasDimensions.width}
          mainCanvasHeight={this.state.canvasDimensions.height}
          backgroundColor={this.state.canvasBackgroundColor}
          frameOrder={this.state.workingFrameOrder}
          framesMarkedForDeletion={this.state.workingDeletedFrameIndices}
          framesMarkedForEmptying={this.state.workingEmptiedFrameIndices}
          onFrameClick={this.handleSelectedFrameChange}
          onFrameDelete={this.handleWorkingDeletedFrameIndicesChange}
          onFrameEmpty={this.handleWorkingEmptiedFrameIndicesChange}
          onOrderChange={this.handleWorkingFrameOrderChange}
          previewImage={this.state.previewImage}
        />
      ) : (
        <PlaceholderSelect onClick={this.handlePlaceholderSelectClick} />
      );
    } else {
      return <PlaceholderSource onClick={this.handlePlaceholderSourceClick} />;
    }
  }

  render() {
    const selectedImages = this.getSelectedImages();
    return (
      <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <div className="App">
          <AppBar position="fixed" elevation={0} className="app-header">
            <Toolbar className="app-header-inner">
              <div className="app-brand">
                <PeaCharacter compact className="app-brand-pea" />
                <Typography component="h1" variant="h6">
                  Peamoji
                </Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<AddAPhoto />}
                onClick={this.handlePlaceholderSourceClick}
                disabled={this.state.imagesAreLoading}
                aria-label="add image"
              >
                Add image
              </Button>
            </Toolbar>
          </AppBar>
          <Snackbar open={this.state.errorMessage !== ''} onClose={this.handleCloseError}>
            <Alert onClose={this.handleCloseError} variant="filled" severity="error">
              {this.state.errorMessage}
            </Alert>
          </Snackbar>
          <main className={`editor-main${this.state.isDrawerOpen ? ' editor-main-shift' : ''}`}>
            <div className="workspace-heading">
              <div>
                <Typography component="h2" variant="h5">
                  Emoji editor
                </Typography>
              </div>
              {this.state.images.length > 0 && (
                <span className="workspace-status">
                  {`${this.state.images.length} ${
                    this.state.images.length === 1 ? 'image' : 'images'
                  } · ${this.state.canvasDimensions.width} × ${this.state.canvasDimensions.height}`}
                </span>
              )}
            </div>
            <section
              className="workspace"
              aria-label="Emoji workspace"
              aria-busy={this.state.imagesAreLoading}
            >
              <div className="workspace-scroll">
                <div className="canvas-content">{this.renderContent()}</div>
              </div>
              {this.state.imagesAreLoading && (
                <div className="loading-overlay" role="status">
                  <CircularProgress disableShrink size={40} aria-label="Processing image" />
                  <Typography>Working on your image…</Typography>
                </div>
              )}
            </section>
            <input
              accept="image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif"
              id="button-file"
              ref={this.fileUpload}
              type="file"
              onChange={this.handleFileChange}
              aria-label="Upload an image"
              hidden
            />
          </main>
          <CompositionModeDialog open={this.state.choosingCompositionMode}
            images={this.state.images} onChoose={this.handleCompositionModeChange} />
          <Toolbox
            imagesAreLoading={this.state.imagesAreLoading}
            images={this.state.images}
            selectedImages={selectedImages}
            selectedFrameIndices={this.state.selectedFrameIndices}
            // Don't display frames that are deleted
            firstSelectedImageFrameOrder={this.getFrameOrderWithoutDeleted()}
            isOpen={this.state.isDrawerOpen}
            onDrawerClose={this.handleDrawerClose}
            activeTool={this.state.activeTool}
            onToolChange={this.handleToolChange}
            onImageCheckboxChange={this.handleImageCheckboxChange}
            onImageSelect={this.handleImageSelect}
            selectedImagesNames={this.state.selectedImagesNames}
            onImageChange={this.handleImageChange}
            onImageChangeStart={this.handleImageLoadStart}
            onImageRemoval={this.handleImageRemoval}
            onImageReorder={this.handleImageReorder}
            selectedPixel={
              this.state.eyeDropperSource === EYE_DROPPER_SOURCE.PARTIFY
                ? this.getSelectedOrHoverPixel()
                : new Uint8ClampedArray()
            }
            eyeDropperIsEnabled={this.state.eyeDropperIsEnabled}
            onEyeDropperClick={this.handleEyeDropperClick}
            onCanvasChange={this.handleCanvasChange}
            canvasDimensions={this.state.canvasDimensions}
            canvasBackgroundColor={this.state.canvasBackgroundColor}
            onFramesChange={this.handleFramesChange}
            onFramesPreviewClick={this.handleFramesPreview}
            onFramesPreviewBackClick={this.handleFramesPreviewExit}
            framesIsPreviewing={this.state.previewImage !== null}
            onImageSelectToggle={this.handleImageSelectToggle}
            isImageSelectOpen={this.state.isImageSelectOpen}
            onAddImageClick={this.handlePlaceholderSourceClick}
          />
        </div>
      </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

export default App;
